import React, { RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import * as mapboxgl from 'mapbox-gl';
import {
    MotionPoint,
    buildDistanceTable,
    headingDeg,
    nearestEquivalentDeg,
    quantizeDeg,
    sampleByDistance,
} from '../../central/delivery-monitor/deliveryMonitorMotion';
import { isLongStay, stopKeys, stopPoint } from './pickupTrackingLogic';
import { STOP_STATUS_COLOR, vehicleThemeOf } from './pickupTrackingTheme';
import { VehicleIcon } from './icons';
import type { TrackingDriver, TrackingSearchScope } from './pickupTrackingTypes';
import styles from './PickupDriverTrackingPage.module.css';

const SOURCE_ID = 'pu-replay';
const LINE_ID = 'pu-replay-line';
const GLOW_ID = 'pu-replay-glow';

// 与原型 playRoute 一致：匀速 5s 跑完；轨迹线与站点一直留着，直到用户点「退出轨迹」
const RUN_MS = 5_000;
// 原型 fitBounds(pts, .35)：先按跨度外扩 35% 再框
const FIT_PAD = 0.35;
const FIT_MAX_ZOOM = 15;
const TIP_WIDTH = 290;
const TIP_GAP = 20;
const TIP_MARGIN = 8;

export interface ReplayRequest {
    /** 回放的司机 key */
    key: string;
    /** 每次点「当日轨迹」/ 搜索打开都 +1，同一司机重复触发也能重放 */
    runId: number;
    /** 搜索命中的站点；从弹窗进入时为 null */
    focus: { idx: number; scope: TrackingSearchScope } | null;
    /** ClickHouse 当日 GPS 轨迹（按 locationTimestampMs 升序，非空）；折线与小车沿它走（同派送大屏） */
    points: MotionPoint[];
}

interface UseStopReplayParams {
    map: mapboxgl.Map | null;
    /** 站点提示框定位的参照系（与地图同原点） */
    areaRef: RefObject<HTMLDivElement>;
    replay: ReplayRequest | null;
    /** replay.key 对应的实时司机数据；站点状态会随轮询变化 */
    driver: TrackingDriver | null;
    thresholdMin: number;
}

interface SiteHost {
    marker: mapboxgl.Marker;
    element: HTMLDivElement;
}

/** 原型 fitBounds(pts, pad)：按跨度外扩 pad 比例；配合 fitBounds(padding: 0) 复现原型的留白 */
export const extendBounds = (
    pts: { lng: number; lat: number }[],
    pad: number,
): [[number, number], [number, number]] => {
    const lngs = pts.map((p) => p.lng);
    const lats = pts.map((p) => p.lat);
    const w = Math.min(...lngs);
    const e = Math.max(...lngs);
    const s = Math.min(...lats);
    const n = Math.max(...lats);
    const dLng = (e - w) * pad;
    const dLat = (n - s) * pad;
    return [
        [w - dLng, s - dLat],
        [e + dLng, n + dLat],
    ];
};

/**
 * 当日轨迹回放（原型 playRoute）：轨迹线沿途匀速画出（line-trim-offset，不逐帧 setData），
 * 每个预约站点一个编号标记，悬停出提示框，搜索命中的站点高亮。
 * 返回的 layer 要渲染在组件树里（portal 到 marker），tooltip 要渲染在 areaRef 里面。
 */
export const useStopReplay = ({
    map,
    areaRef,
    replay,
    driver,
    thresholdMin,
}: UseStopReplayParams): { layer: React.ReactNode; tooltip: React.ReactNode } => {
    const { t } = useTranslation();
    const [, forceRerender] = useState(0);
    // marker / 悬停 / 命中都按站点 key（stopKeys）记：站点按上门顺序排，轮询时下标会挪，编号取实时下标
    const [hoverKey, setHoverKey] = useState<string | null>(null);
    const [focusKey, setFocusKey] = useState<string | null>(null);
    const [carHost, setCarHost] = useState<HTMLDivElement | null>(null);
    const sitesRef = useRef<Map<string, SiteHost>>(new Map());
    const tipRef = useRef<HTMLDivElement>(null);
    const driverRef = useRef(driver);
    driverRef.current = driver;
    const focusRef = useRef(replay?.focus ?? null);
    focusRef.current = replay?.focus ?? null;
    const pointsRef = useRef(replay?.points ?? null);
    pointsRef.current = replay?.points ?? null;

    const runId = replay?.runId ?? null;
    const replayKey = replay?.key ?? null;

    useEffect(() => {
        const d = driverRef.current;
        const motion = pointsRef.current;
        if (runId === null || !map || !d || d.key !== replayKey || !motion || motion.length === 0) return;

        const sites = sitesRef.current;
        // 框住轨迹和站点：站点在 address_core 坐标上，可能偏离 GPS 轨迹
        const located = d.stops.map(stopPoint).filter((p): p is { lng: number; lat: number } => p !== null);
        map.fitBounds(extendBounds([...motion, ...located], FIT_PAD), { padding: 0, maxZoom: FIT_MAX_ZOOM, duration: 700 });

        const color = vehicleThemeOf(d.vehicleType).color;
        const coords = motion.map((p) => [p.lng, p.lat] as [number, number]);
        // LineString 至少 2 个点；只有 1 个点时原地复制一份
        const lineCoords = coords.length === 1 ? [coords[0], coords[0]] : coords;
        map.addSource(SOURCE_ID, {
            type: 'geojson',
            lineMetrics: true,
            data: {
                type: 'Feature',
                properties: {},
                geometry: { type: 'LineString', coordinates: lineCoords },
            },
        });
        const layout = { 'line-cap': 'round' as const, 'line-join': 'round' as const };
        map.addLayer({
            id: GLOW_ID,
            type: 'line',
            source: SOURCE_ID,
            layout,
            paint: { 'line-color': color, 'line-width': 11, 'line-opacity': 0.16, 'line-trim-offset': [0, 1] },
        });
        map.addLayer({
            id: LINE_ID,
            type: 'line',
            source: SOURCE_ID,
            layout,
            paint: { 'line-color': color, 'line-width': 3.4, 'line-opacity': 0.95, 'line-trim-offset': [0, 1] },
        });

        const keys = stopKeys(d.stops);
        d.stops.forEach((s, i) => {
            const at = stopPoint(s);
            if (!at) return;
            const element = document.createElement('div');
            element.style.cssText = 'width:24px;height:24px;';
            const marker = new mapboxgl.Marker({ element, anchor: 'center' }).setLngLat([at.lng, at.lat]).addTo(map);
            sites.set(keys[i], { marker, element });
        });

        const theme = vehicleThemeOf(d.vehicleType);
        const carEl = document.createElement('div');
        carEl.style.cssText = `width:34px;height:${theme.height}px;z-index:200000;pointer-events:none;`;
        const rotor = document.createElement('div');
        rotor.style.cssText = `width:34px;height:${theme.height}px;`;
        carEl.appendChild(rotor);
        const carMarker = new mapboxgl.Marker({ element: carEl, anchor: 'center' }).setLngLat(coords[0]).addTo(map);
        setCarHost(rotor);
        // 搜索命中的下标是按触发时的列表算的，这里换成 key，之后轮询换序也能高亮同一站
        const hitIdx = focusRef.current?.idx;
        const hitKey = hitIdx !== undefined ? keys[hitIdx] ?? null : null;
        setFocusKey(hitKey);
        setHoverKey(hitKey);
        forceRerender((n) => n + 1);

        const table = buildDistanceTable(motion);
        const t0 = performance.now();
        let rotation: number | null = null;
        let raf = 0;
        const step = (now: number) => {
            const p = Math.min(1, (now - t0) / RUN_MS);
            const s = sampleByDistance(motion, table, p);
            carMarker.setLngLat([s.lng, s.lat]);
            // [p, 1] 区间被裁掉，露出的就是已走过的部分
            map.setPaintProperty(LINE_ID, 'line-trim-offset', [p, 1]);
            map.setPaintProperty(GLOW_ID, 'line-trim-offset', [p, 1]);
            const next = motion[s.segment + 1];
            const heading = next ? headingDeg(motion[s.segment], next) : null;
            if (heading !== null) {
                const deg = nearestEquivalentDeg(rotation, quantizeDeg(heading, 5));
                if (deg !== rotation) {
                    rotor.style.transform = `rotate(${deg}deg)`;
                    rotation = deg;
                }
            }
            raf = p < 1 ? requestAnimationFrame(step) : 0;
        };
        raf = requestAnimationFrame(step);

        return () => {
            cancelAnimationFrame(raf);
            carMarker.remove();
            sites.forEach((h) => h.marker.remove());
            sites.clear();
            setCarHost(null);
            setHoverKey(null);
            setFocusKey(null);
            // 地图可能已先销毁（卸载顺序），此时图层随地图一起没了
            try {
                [LINE_ID, GLOW_ID].forEach((id) => {
                    if (map.getLayer(id)) map.removeLayer(id);
                });
                if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
            } catch {
                // map already removed
            }
        };
    }, [map, runId, replayKey]);

    const liveKeys = driver ? stopKeys(driver.stops) : [];

    // 站点 marker 的层叠：命中 > 悬停 > 序号
    useEffect(() => {
        liveKeys.forEach((k, i) => {
            const h = sitesRef.current.get(k);
            if (!h) return;
            h.element.style.zIndex = String(150000 + i + (focusKey === k ? 5000 : 0) + (hoverKey === k ? 1000 : 0));
        });
    });

    // 提示框贴着悬停的站点：右侧 +20px，右侧放不下翻到左侧，纵向夹在地图内
    useEffect(() => {
        if (hoverKey === null || !map) return;
        let raf = 0;
        let last = '';
        const loop = () => {
            const el = tipRef.current;
            const area = areaRef.current;
            const host = sitesRef.current.get(hoverKey);
            if (el && area && host) {
                const p = map.project(host.marker.getLngLat());
                let x = p.x + TIP_GAP;
                if (x + TIP_WIDTH > area.clientWidth - TIP_MARGIN) x = p.x - TIP_GAP - TIP_WIDTH;
                const y = Math.max(TIP_MARGIN, Math.min(p.y - 24, area.clientHeight - el.offsetHeight - TIP_MARGIN));
                const key = `${Math.round(x)},${Math.round(y)}`;
                if (key !== last) {
                    el.style.left = `${Math.round(x)}px`;
                    el.style.top = `${Math.round(y)}px`;
                    el.style.visibility = 'visible';
                    last = key;
                }
            }
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, [map, hoverKey, areaRef, runId]);

    if (runId === null || !driver || driver.key !== replayKey) return { layer: null, tooltip: null };

    const focus = replay?.focus ?? null;
    const statusLabel = (status: string) => t(`pickup.driverTracking.status.${status}`);
    const stayText = (stayMin: number, long: boolean) =>
        t(long ? 'pickup.driverTracking.stayShortLong' : 'pickup.driverTracking.stayShort', { min: stayMin });

    // 不 memo：site host 在 effect 里才创建，effect 跑完 forceRerender 才能写进 portal（同派送大屏 marker 的做法）
    const siteNodes: React.ReactNode[] = [];
    driver.stops.forEach((s, i) => {
        const k = liveKeys[i];
        const host = sitesRef.current.get(k);
        if (!host) return;
        const long = isLongStay(s, thresholdMin);
        const hit = focus !== null && focusKey === k;
        const showStay = s.status !== 'wait' && s.stayMin !== null;
        const cls = [
            styles.site,
            s.status === 'ok' ? styles.sOk : s.status === 'fail' ? styles.sFail : s.status === 'cur' ? styles.sCur : styles.sWait,
            hoverKey === k ? styles.siteHov : '',
            hit ? styles.siteHit : '',
            focus !== null && !hit ? styles.siteDim : '',
        ]
            .filter(Boolean)
            .join(' ');
        siteNodes.push(
            createPortal(
                <button
                    type="button"
                    className={cls}
                    aria-label={t('pickup.driverTracking.siteAria', {
                        no: i + 1,
                        site: s.siteName,
                        status: statusLabel(s.status),
                    }) || ''}
                    onMouseEnter={() => setHoverKey(k)}
                    onMouseLeave={() => setHoverKey((cur) => (cur === k ? null : cur))}
                    onFocus={() => setHoverKey(k)}
                    onBlur={() => setHoverKey((cur) => (cur === k ? null : cur))}
                    onClick={(e) => e.stopPropagation()}
                >
                    {hit && (
                        <>
                            <i className={styles.hitring} />
                            <i className={`${styles.hitring} ${styles.hitringB}`} />
                            <i className={styles.hitcross} />
                            <span className={styles.hitlbl}>
                                {t('pickup.driverTracking.searchHit')}
                                <em>{focus?.scope === 'addr' ? s.address : `${s.siteName} · ${s.siteId}`}</em>
                            </span>
                        </>
                    )}
                    {long && (
                        <>
                            <i className={styles.sring} />
                            <span className={styles.sbadge}>!</span>
                        </>
                    )}
                    <span className={`${styles.siteDot} ${styles.num}`}>{i + 1}</span>
                    {showStay && (
                        <span className={`${styles.siteStay} ${long ? styles.siteStayLong : ''}`}>
                            {stayText(s.stayMin ?? 0, long)}
                        </span>
                    )}
                </button>,
                host.element,
                `site-${k}`,
            ),
        );
    });

    const carNode = carHost
        ? createPortal(
              <VehicleIcon
                  type={driver.vehicleType}
                  color={vehicleThemeOf(driver.vehicleType).color}
                  selected
              />,
              carHost,
              'replay-car',
          )
        : null;

    const tipIdx = hoverKey !== null ? liveKeys.indexOf(hoverKey) : -1;
    const tipStop = tipIdx >= 0 ? driver.stops[tipIdx] : undefined;
    const tipLong = tipStop ? isLongStay(tipStop, thresholdMin) : false;
    const tooltip = tipStop ? (
        <div ref={tipRef} className={styles.stip} key={`tip-${runId}`}>
            <div className={styles.stipHead}>
                <span className={`${styles.stipNo} ${styles.num}`}>#{tipIdx + 1}</span>
                <b className={styles.stipName}>{tipStop.siteName}</b>
                <span className={styles.stipSt} style={{ color: STOP_STATUS_COLOR[tipStop.status] }}>
                    {statusLabel(tipStop.status)}
                </span>
            </div>
            <div className={styles.stipB}>
                <div>
                    <span>{t('pickup.driverTracking.siteId')}</span>
                    <b className={styles.num}>{tipStop.siteId}</b>
                </div>
                <div>
                    <span>{t('pickup.driverTracking.addr')}</span>
                    <b>{tipStop.address}</b>
                </div>
                <div>
                    <span>{t('pickup.driverTracking.expect')}</span>
                    <b className={styles.num}>{tipStop.expectWindow ?? '—'}</b>
                </div>
                <div>
                    <span>{t('pickup.driverTracking.colStay')}</span>
                    <b className={tipLong ? styles.long : ''}>
                        {tipStop.status === 'wait' || tipStop.stayMin === null
                            ? '—'
                            : t(tipLong ? 'pickup.driverTracking.stayLong' : 'pickup.driverTracking.stayMinutes', {
                                  min: tipStop.stayMin,
                              })}
                    </b>
                </div>
                <div>
                    <span>{t('pickup.driverTracking.colTime')}</span>
                    <b className={styles.num}>{tipStop.operatedAt ?? '—'}</b>
                </div>
            </div>
        </div>
    ) : null;

    return {
        layer: (
            <>
                {siteNodes}
                {carNode}
            </>
        ),
        tooltip,
    };
};
